import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  createEnrollment,
  getEnrollmentForTrial,
  sendEnrollmentOffer,
  type CreateEnrollmentPayload,
  type EnrollmentDTO,
} from '../../lib/enrollments';
import { masterdataApi, type UnterrichtsformItem, kurseApi, type KursItem } from '../../lib/courses';
import { api, historyTemplatesApi } from '../../lib/api';
import { formatDate, toApiDate } from '../../lib/dateFormat';
import { personenApi, type TeacherAvailabilitySlot } from '../../lib/personen';
import { toast } from 'sonner';
import { type PreviewEvent, type TeacherTimetableSelectEvent } from '../schedule/TeacherTimetable';
import WizardShell from '../ui/wizard-shell';
import { cn } from '../../lib/utils';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';
import CommunicationStandortHint from '../communications/CommunicationStandortHint';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem } from '../ui/wizard-layout';
import WizardScheduleGrid from '../schedule/WizardScheduleGrid';
import { getSubjectLabels } from '../../lib/settings';
import StepDateSelection from '../trials/trialWizard/StepDateSelection';
import type { DatePageModel, SelectedSlot, Step1Section } from '../trials/trialWizard/types';
import { ChevronDown } from 'lucide-react';
import { Switch } from '../ui/switch';
import { billingApi, type EnrollmentBillingOptions } from '../../lib/api/billing';
import { PERIOD_LABEL, formatCents, installmentLabel } from '../../lib/billing';
import ContingentTariffStep from './ContingentTariffStep';
import EinmalcodeFeld from './EinmalcodeFeld';
import PeriodicTariffStep from './PeriodicTariffStep';

interface UnterrichtsformArtItem {
  id: number;
  code: string;
  name: string;
  price_per_60_cents: number;
  currency: string;
  trial_lesson_possible?: boolean;
  trial_lesson_duration_minutes?: number | null;
}

interface AccountingPackage {
  id: string;
  name: string;
  units: number;
  discountPercent?: number; // Optional für Rückwärtskompatibilität
  installmentMonths: number | null;
  features: string[];
  highlighted: boolean;
  highlightLabel: string | null;
  special?: boolean;
  // Neue Felder
  immediatePriceMode?: 'total' | 'per_unit';
  immediatePriceCents?: number;
  installmentPriceMode?: 'total' | 'per_unit';
  installmentPriceCents?: number;
  totalCentsImmediate?: number;
  pricePerUnitCentsImmediate?: number;
  totalCentsInstallment?: number;
  pricePerUnitCentsInstallment?: number;
  immediateDiscountPercent?: number;
  installmentEnabled?: boolean;
  defaultPaymentType?: 'immediate' | 'installment';
  signupFeeMode?: 'always' | 'never' | 'first_abo';
  signupFeeIncluded?: boolean;
}

interface PackagePrice {
  package: AccountingPackage;
  pricePerUnitCents: number; // Nach Rabatt
  originalPricePerUnitCents: number; // Vor Rabatt
  totalCents: number; // Nach Rabatt
  originalTotalCents: number; // Vor Rabatt
  currency: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trialId: string | number;
  trialData?: any; // Optional: Trial-Daten direkt übergeben
  subprofileId?: string;
  teacherId?: number;
  defaults?: { weekday?: number; startTime?: string; startDate?: string; unterrichtsformId?: number; intervalWeeks?: number };
  onCreated?: (id: number) => void;
}

type HistoryTaskPreview = {
  enabled: boolean;
  description?: string;
  dueAt?: string;
} | null;

const toHHMM = (d: Date): string => {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const addMinutes = (hhmm: string, minutes: number): string => {
  const [h, m] = hhmm.split(':').map(Number);
  const base = new Date(1970, 0, 1, h || 0, m || 0, 0);
  base.setMinutes(base.getMinutes() + minutes);
  return toHHMM(base);
};

const addDaysYmd = (ymd: string, days: number): string => {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toApiDate(d);
};

const computeDatePageModel = (
  selectedSlot: SelectedSlot,
  nextAvailableDate: string,
  datePage: number,
  datesPerPage: number
): DatePageModel => {
  if (!selectedSlot) return null;
  const today = toApiDate(new Date());
  const startDate = addDaysYmd(today, -56);
  const endDate = addDaysYmd(today, 84);

  const all: string[] = [];
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const cand = toApiDate(d);
    const dow = ((d.getDay() + 6) % 7) + 1;
    if (dow === selectedSlot.dayOfWeek) all.push(cand);
  }

  const anchor = nextAvailableDate || today;
  const anchorIdxRaw = all.findIndex((d) => d >= anchor);
  const anchorIdx = Math.max(0, anchorIdxRaw);

  const minPage = -Math.floor(anchorIdx / datesPerPage);
  const maxPage = Math.max(minPage, Math.ceil((all.length - anchorIdx) / datesPerPage) - 1);
  const effectivePage = Math.min(maxPage, Math.max(minPage, datePage));

  const pageIndex = effectivePage - minPage;
  const totalPages = maxPage - minPage + 1;
  const startIdx = anchorIdx + effectivePage * datesPerPage;
  const pageDates = all.slice(Math.max(0, startIdx), Math.max(0, startIdx) + datesPerPage);

  return {
    effectivePage,
    canPrev: effectivePage > minPage,
    canNext: effectivePage < maxPage,
    pageLabel: `Seite ${pageIndex + 1} von ${totalPages}`,
    pageDates: pageDates.map((d) => ({ d, isBeforeAnchor: d < anchor, isPast: d < today })),
  };
};

/**
 * Die Preis-Platzhalter für die Vorschau im Kommunikationsschritt.
 *
 * Gerechnet wird wie im Backend (`_paket_variables`): Das Startpaket ist immer
 * Vorkasse, die letzte Rate trägt den Rest-Cent. Verschickt wird am Ende der
 * Servertext — hier geht es nur darum, dass im Editor kein roher Platzhalter steht.
 */
function paketPlatzhalter(args: {
  billingOptions: EnrollmentBillingOptions | null;
  contingentTariffId?: number;
  packagePrices: PackagePrice[];
  includeStartPackage: boolean;
  /** Das im Gespräch gewählte Folgepaket (T-0230) — gilt, wenn kein Startpaket dabei ist. */
  vorgewaehltesPaket?: string;
}): Record<string, string> {
  const leer = {
    paketName: '',
    paketPreis: '',
    paketEinheiten: '',
    zahlweise: '',
    ratenText: '',
    preisZeile: '',
  };
  const { billingOptions, contingentTariffId, packagePrices, includeStartPackage, vorgewaehltesPaket } = args;

  if (billingOptions?.model === 'periodic' && billingOptions.tariff) {
    const tariff = billingOptions.tariff;
    const monate = tariff.pricePeriodMonths ?? 1;
    const periode = PERIOD_LABEL[monate] ?? `${monate} Monate`;
    return {
      ...leer,
      paketName: tariff.name,
      paketPreis: formatCents(tariff.priceCents),
      zahlweise: 'Beitrag',
      preisZeile: `${formatCents(tariff.priceCents)} je ${periode}`,
    };
  }

  const tariff = (billingOptions?.contingentTariffs ?? []).find((t) => t.id === contingentTariffId);
  if (tariff) {
    const einheiten = tariff.units ?? 0;
    const raten = tariff.contingentMode === 'installments' ? (tariff.installmentCount ?? 0) : 0;
    const ratenText = raten >= 2 ? installmentLabel(tariff.priceCents, raten) : '';
    const basis = einheiten
      ? `${formatCents(tariff.priceCents)} für ${einheiten} Einheiten`
      : formatCents(tariff.priceCents);
    return {
      paketName: tariff.name,
      paketPreis: formatCents(tariff.priceCents),
      paketEinheiten: einheiten ? String(einheiten) : '',
      zahlweise: raten >= 2 ? `${raten} Raten` : 'Vorkasse',
      ratenText,
      preisZeile: ratenText ? `${basis}, zahlbar in ${ratenText} monatlich` : basis,
    };
  }

  // Altpakete: gebucht wird das erste Sonderpaket als Startpaket, immer auf Vorkasse.
  // Ohne Startpaket das im Gespräch gewählte Folgepaket, wie der Server (T-0277).
  const start = (includeStartPackage ? packagePrices.find((pp) => pp.package.special) : undefined)
    ?? packagePrices.find((pp) => !pp.package.special && pp.package.id === vorgewaehltesPaket);
  if (!start) return leer;
  const einheiten = start.package.units ?? 0;
  return {
    ...leer,
    paketName: start.package.name,
    paketPreis: formatCents(start.totalCents),
    paketEinheiten: einheiten ? String(einheiten) : '',
    zahlweise: 'Vorkasse',
    preisZeile: einheiten
      ? `${formatCents(start.totalCents)} für ${einheiten} Einheiten`
      : formatCents(start.totalCents),
  };
}

/** Solange die Anmeldung noch nicht läuft, passt der Assistent sie an (Backend gleichauf). */
const ANPASSBARE_STATUS = new Set(['draft', 'sent', 'accepted_by_student']);

const EnrollmentWizardModal: React.FC<Props> = ({
  open,
  onOpenChange,
  trialId,
  trialData: propTrialData,
  subprofileId,
  teacherId,
  defaults,
  onCreated
}) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Schritt 1: Terminplan-Auswahl
  const [step1ActiveSection, setStep1ActiveSection] = useState<Step1Section>('unterricht');
  const step1UnterrichtCollapsed = step1ActiveSection !== 'unterricht';
  const step1TerminplanCollapsed = step1ActiveSection !== 'terminplan';
  const [courseOptions, setCourseOptions] = useState<KursItem[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>();
  // Gibt es schon eine Anmeldung, passt der Assistent sie an, statt eine zweite
  // anzulegen. Der Titel und der Knopf sagen das dann auch.
  const [bestehendeAnmeldung, setBestehendeAnmeldung] = useState<EnrollmentDTO | null>(null);
  // Beginn eines übernommenen Kurses: vorher kann der Unterricht nicht starten.
  const [courseStartDate, setCourseStartDate] = useState<string>('');
  const [selectedFormId, setSelectedFormId] = useState<number | undefined>(defaults?.unterrichtsformId);
  const [weekday, setWeekday] = useState<number | undefined>(defaults?.weekday);
  const [startTime, setStartTime] = useState<string>(defaults?.startTime?.slice(0, 5) || '');
  const [startDate, setStartDate] = useState<string>(defaults?.startDate || '');
  const [interval, setInterval] = useState<number>(defaults?.intervalWeeks || 1);
  const [durationMin, setDurationMin] = useState<number | undefined>();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [datePage, setDatePage] = useState<number>(0);
  const datesPerPage = 4;
  const [nextAvailableDate, setNextAvailableDate] = useState<string>('');
  const [dateManuallySelected, setDateManuallySelected] = useState<boolean>(false);

  // Schritt 3: Paket-Info (Office)
  const [packagePrices, setPackagePrices] = useState<PackagePrice[]>([]);
  const [includeStartPackage, setIncludeStartPackage] = useState<boolean>(true);
  const [includeSignupFee, setIncludeSignupFee] = useState<boolean>(true);
  const [tarifAufnahmegebuehr, setTarifAufnahmegebuehr] = useState<boolean>(false);
  const [paymentTypeByPackage, setPaymentTypeByPackage] = useState<Record<string, 'sofort' | 'raten'>>({});
  const [signupFeeAmountCents, setSignupFeeAmountCents] = useState<number>(0);
  // Beitrag (ADR-0003): Modell, Tarif und Zahlweise statt Paket-Informationen
  const [billingOptions, setBillingOptions] = useState<EnrollmentBillingOptions | null>(null);
  const [contingentTariffId, setContingentTariffId] = useState<number | undefined>(undefined);
  const [billingInterval, setBillingInterval] = useState<number | undefined>();
  const [teacherAvailability, setTeacherAvailability] = useState<TeacherAvailabilitySlot[]>([]);

  // Schritt 3: E-Mail
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [emailContent, setEmailContent] = useState<string>('');
  const [sendToStudent, setSendToStudent] = useState<boolean>(true);
  const [skipStudentEmail, setSkipStudentEmail] = useState<boolean>(false);
  const [skipStudentSms, setSkipStudentSms] = useState<boolean>(true);
  const [createHistoryText, setCreateHistoryText] = useState<string>('');
  const [createHistoryTouched, setCreateHistoryTouched] = useState<boolean>(false);
  const [historyTaskPreview, setHistoryTaskPreview] = useState<HistoryTaskPreview>(null);

  // Daten
  const [unterrichtsformen, setUnterrichtsformen] = useState<UnterrichtsformItem[]>([]);
  const [unterrichtsformArten, setUnterrichtsformArten] = useState<UnterrichtsformArtItem[]>([]);
  const [selectedForm, setSelectedForm] = useState<UnterrichtsformItem | null>(null);
  const [trialData, setTrialData] = useState<any>(null);
  // Die Probestunde, aus der diese Anmeldung entsteht, gehört nicht in den
  // Terminplan des neuen Kurses: sie stand dort grün neben dem gelben Vorschlag
  // und sah aus wie ein zweiter Termin (T-0011).
  const versteckteTermine = useMemo(() => [`trial-${trialId}`], [trialId]);
  const standortId = useMemo(() => {
    const raw = (trialData as any)?.standort_id ?? (trialData as any)?.standortId ?? null;
    const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : raw;
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
  }, [trialData]);

  // Kursdaten laden (für Event-Klick/Übernahme)
  useEffect(() => {
    const loadCourses = async () => {
      if (!open || !teacherId || !trialData?.fachId) {
        setCourseOptions([]);
        return;
      }
      try {
        // teacherId ist WP-User-ID, Backend mappt automatisch zu Lehrer-Stamm-ID
        const courses = await kurseApi.list({ fachId: trialData.fachId, teacherId });
        setCourseOptions(courses || []);
      } catch {
        setCourseOptions([]);
      }
    };
    loadCourses();
  }, [open, teacherId, trialData?.fachId]);

  // Laden der Daten beim Öffnen
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    // Nur die Formen, die am Standort der Probestunde angeboten werden (STO-3.2).
    const rohStandort = (propTrialData as any)?.standort_id ?? (propTrialData as any)?.standortId ?? null;
    const formStandort = Number.parseInt(String(rohStandort ?? ''), 10);
    Promise.all([
      masterdataApi.unterrichtsformen(Number.isFinite(formStandort) && formStandort > 0 ? formStandort : null),
      api.get<UnterrichtsformArtItem[]>('/api/unterrichtsform-arten'),
      trialId ? getEnrollmentForTrial(trialId).catch(() => null) : Promise.resolve(null),
    ]).then(([forms, arten, vorhanden]) => {
      if (cancelled) return;
      setUnterrichtsformen(forms || []);
      setUnterrichtsformArten(arten || []);
      setTrialData(propTrialData || null);
      // Pakete werden später basierend auf ausgewählter Unterrichtsform geladen

      // Eine noch nicht laufende Anmeldung wird angepasst — ihre Werte gewinnen über
      // die Vorgaben der Probestunde. Vorher öffnete „Anmeldung anpassen" leer und
      // legte beim Speichern eine zweite Anmeldung an.
      const anpassbar = vorhanden && ANPASSBARE_STATUS.has(String(vorhanden.status || ''));
      setBestehendeAnmeldung(anpassbar ? vorhanden : null);
      const quelle = anpassbar ? vorhanden : null;

      const formId = quelle?.unterrichtsformId ?? defaults?.unterrichtsformId;
      if (formId) {
        const form = forms.find(f => f.id === formId);
        if (form) {
          setSelectedForm(form);
          setDurationMin(quelle?.durationMin || form.dauer);
        }
      }
      const datum = quelle?.startDate ?? defaults?.startDate;
      if (datum) {
        setStartDate(datum);
      }
      if (quelle) {
        if (quelle.weekday) setWeekday(Number(quelle.weekday));
        if (quelle.startTime) setStartTime(String(quelle.startTime).slice(0, 5));
        if (quelle.intervalWeeks) setInterval(Number(quelle.intervalWeeks));
        // Nur einen übernommenen Kurs behalten. Eine Reservierung entsteht neu,
        // sonst bliebe ihre Terminregel auf dem alten Slot stehen.
        if (quelle.courseId && !quelle.courseReserved) setSelectedCourseId(Number(quelle.courseId));
        if (quelle.billingTariffId) setContingentTariffId(Number(quelle.billingTariffId));
        if (quelle.billingIntervalMonths) setBillingInterval(Number(quelle.billingIntervalMonths));
        if (quelle.emailSubject) setEmailSubject(String(quelle.emailSubject));
        if (quelle.emailContent) setEmailContent(String(quelle.emailContent));
        // Einmalcode und Zahlweise blieben beim Anpassen bisher nicht erhalten (T-0011).
        setEinmalcode(quelle.couponCode ? String(quelle.couponCode) : '');
        setVorgewaehltesPaket(quelle.vorgewaehltesPaket ? String(quelle.vorgewaehltesPaket) : '');
        // Ob das Startpaket im Angebot war, ging beim Anpassen bisher verloren (T-0247).
        if (typeof quelle.startpaketSelected === 'boolean') setIncludeStartPackage(quelle.startpaketSelected);
        if (quelle.zahlweiseJePaket) {
          setPaymentTypeByPackage(
            Object.fromEntries(
              Object.entries(quelle.zahlweiseJePaket).map(([id, art]) => [id, art === 'installment' ? 'raten' : 'sofort']),
            ),
          );
        }
        // Der Termin steht damit fest; sonst überschreibt ihn der erste freie Slot.
        setDateManuallySelected(true);
      }
    }).catch(err => {
      if (cancelled) return;
      toast.error('Fehler beim Laden der Daten: ' + (err.message || 'Unbekannter Fehler'));
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, trialId, defaults, propTrialData]);

  useEffect(() => {
    if (!open || !teacherId || !standortId) {
      setTeacherAvailability([]);
      return;
    }
    let cancelled = false;
    personenApi
      .getTeacherAvailability(teacherId, standortId)
      .then((slots) => {
        if (!cancelled) setTeacherAvailability(Array.isArray(slots) ? slots : []);
      })
      .catch(() => {
        if (!cancelled) setTeacherAvailability([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, teacherId, standortId]);

  // Unterrichtsform ändern → Dauer aktualisieren
  useEffect(() => {
    if (selectedFormId) {
      const form = unterrichtsformen.find(f => f.id === selectedFormId);
      if (form) {
        setSelectedForm(form);
        setDurationMin(form.dauer);
      }
    } else {
      setSelectedForm(null);
      setDurationMin(undefined);
    }
  }, [selectedFormId, unterrichtsformen]);

  // Pakete für ausgewählte Unterrichtsform laden. Geladen wird beim ersten
  // Erreichen von Schritt 3; danach bleiben Preise und die gewählte Zahlweise
  // stehen, bis sich Unterrichtsform oder Dauer ändern. Früher wurde beim
  // Verlassen von Schritt 3 geleert — die Anmeldung ging dann ohne
  // `zahlweiseJePaket` und `contingentPackageId` hinaus.
  const geladenePaketeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedFormId || !durationMin) {
      geladenePaketeRef.current = null;
      setPackagePrices([]);
      setPaymentTypeByPackage({});
      return;
    }
    if (step < 3) return;
    const schluessel = `${selectedFormId}:${durationMin}:${standortId}:${selectedForm?.art_id ?? ''}:${unterrichtsformArten.length}`;
    if (geladenePaketeRef.current === schluessel) return;
    geladenePaketeRef.current = schluessel;

    const loadPackages = async () => {
      try {
        if (!selectedFormId) return;

        // Entscheidung F08: Preise und Verfügbarkeit gelten je Standort.
        const formPackages = await api.get<AccountingPackage[]>(
          `/api/unterrichtsformen/${selectedFormId}/packages${standortId > 0 ? `?standortId=${standortId}` : ''}`,
        );

        // Preise für UI berechnen
        const art = unterrichtsformArten.find(a => selectedForm?.art_id === a.id);
        if (!art) {
          geladenePaketeRef.current = null;
          return;
        }

        const pricePer60 = art.price_per_60_cents;
        const factor = Math.max(0.25, durationMin / 60.0);
        const basePricePerUnit = Math.round(pricePer60 * factor);

        // Sortiere: Special-Pakete zuerst
        const sortedPackages = [...(formPackages || [])].sort((a: any, b: any) => {
          const aSpecial = a.special === true ? 1 : 0;
          const bSpecial = b.special === true ? 1 : 0;
          if (aSpecial !== bSpecial) {
            return bSpecial - aSpecial; // Special zuerst
          }
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        });

        const prices: PackagePrice[] = sortedPackages.map((pkg: any) => {
          const totalCentsImmediate = pkg.totalCentsImmediate || pkg.immediatePriceCents || 0;
          const configuredDiscountPercent = Math.max(
            0,
            Math.round(Number(pkg.immediateDiscountPercent ?? pkg.discountPercent ?? 0))
          );
          const totalCentsInstallment = pkg.totalCentsInstallment || pkg.installmentPriceCents || 0;
          const originalTotal = totalCentsInstallment > totalCentsImmediate
            ? totalCentsInstallment
            : configuredDiscountPercent > 0
              ? Math.round(totalCentsImmediate / (1 - (configuredDiscountPercent / 100)))
              : pkg.units * basePricePerUnit;

          return {
            package: pkg,
            pricePerUnitCents: pkg.units > 0 ? Math.round(totalCentsImmediate / pkg.units) : 0,
            originalPricePerUnitCents: pkg.units > 0 ? Math.round(originalTotal / pkg.units) : 0,
            totalCents: totalCentsImmediate,
            originalTotalCents: originalTotal,
            currency: art.currency || 'EUR',
          };
        });

        setPackagePrices(prices);
        setPaymentTypeByPackage(() => {
          const next: Record<string, 'sofort' | 'raten'> = {};
          for (const pkg of sortedPackages) {
            next[pkg.id] = pkg.defaultPaymentType === 'installment' ? 'raten' : 'sofort';
          }
          return next;
        });
      } catch (err) {
        console.error('Fehler beim Laden der Pakete:', err);
        geladenePaketeRef.current = null;
        setPackagePrices([]);
        setPaymentTypeByPackage({});
      }
    };

    loadPackages();
  }, [step, selectedFormId, durationMin, unterrichtsformArten, selectedForm, standortId]);

  useEffect(() => {
    if (!open || step !== 3 || !selectedFormId) {
      setSignupFeeAmountCents(0);
      return;
    }
    let cancelled = false;
    const loadFeeConfig = async () => {
      try {
        // Aufnahmegebühr der Unterrichtsform mit Standort-Abweichung — dieselbe Quelle
        // wie die Website und die Buchung beim Vertragsstart (T-0054).
        const params = new URLSearchParams({ unterrichtsformId: String(selectedFormId) });
        if (standortId > 0) params.set('standortId', String(standortId));
        const fee = await api.get<{ amountCents: number }>(`/api/enrollments/signup-fee?${params.toString()}`);
        if (cancelled) return;
        const cents = Number(fee?.amountCents || 0);
        setSignupFeeAmountCents(Number.isFinite(cents) && cents > 0 ? cents : 0);
      } catch {
        if (!cancelled) setSignupFeeAmountCents(0);
      }
    };
    void loadFeeConfig();
    return () => {
      cancelled = true;
    };
  }, [open, step, standortId, selectedFormId]);

  /** Die Portal-ID des Kontos — der Präfix der Profil-Kennung. */
  const kundenWpUserId = useMemo(() => {
    const sp = String(subprofileId || '').trim();
    if (!sp) return 0;
    return Number.parseInt(sp.includes('-') ? sp.split('-', 1)[0] : sp, 10) || 0;
  }, [subprofileId]);
  const [einmalcode, setEinmalcode] = useState<string>('');
  // Paket, für das sich der Kunde im Gespräch entschieden hat (T-0230).
  const [vorgewaehltesPaket, setVorgewaehltesPaket] = useState<string>('');

  const composerGroups: RecipientGroup[] = useMemo(() => {
    const sp = String(subprofileId || '').trim();
    if (!sp) return [];
    let wpUserId = 0;
    let subIndex: number | null = null;
    if (sp.includes('-')) {
      const [a, b] = sp.split('-', 2);
      wpUserId = Number.parseInt(a, 10) || 0;
      subIndex = b ? (Number.parseInt(b, 10) || null) : null;
    } else {
      wpUserId = Number.parseInt(sp, 10) || 0;
    }
    if (wpUserId <= 0) return [];
    return [{
      key: 'student',
      label: 'Teilnehmer',
      recipients: [{
        id: `person:${wpUserId}:${subIndex ?? ''}`,
        type: 'person' as const,
        wpUserId,
        subIndex,
        name: (trialData as any)?.studentName || 'Teilnehmer',
      }],
      templateKeys: { email: 'enrollment.offer.student' },
      vars: {
        // Fach, Unterrichtsform und Lehrkraft füllt beim Versand der Server; die
        // Vorschau ließ sie leer, als käme die Mail so an (T-0277).
        fachName: trialData?.fachName || '',
        unterrichtsformName: unterrichtsformen.find((f) => f.id === selectedFormId)?.name || '',
        unterrichtsform: unterrichtsformen.find((f) => f.id === selectedFormId)?.name || '',
        teacherName: trialData?.teacherName || '',
        startDate: startDate || toApiDate(new Date()),
        weekdayName: ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'][(weekday || 1) - 1],
        startTime: startTime || '15:00',
        enrollmentLink: '#enrollment-link-placeholder',
        ...paketPlatzhalter({ billingOptions, contingentTariffId, packagePrices, includeStartPackage, vorgewaehltesPaket }),
        // Vorschau der Aufnahmegebühr; verschickt wird der Wert vom Server (T-0171).
        aufnahmegebuehr: signupFeeAmountCents > 0 && (billingOptions?.model === 'periodic' || (billingOptions?.model === 'contingent' && (billingOptions.contingentTariffs ?? []).length > 0) ? tarifAufnahmegebuehr : includeSignupFee) ? formatCents(signupFeeAmountCents) : '',
      },
      // Enrollment-Angebote sind transactional; E-Mail bleibt hier bewusst standardmäßig aktiv,
      // damit asynchrone Draft-Updates keinen Composer-Reset-Loop auslösen.
      defaultSelectedChannels: { email: true, sms: false },
    }];
  }, [subprofileId, trialData, startDate, weekday, startTime, billingOptions, contingentTariffId, packagePrices, includeStartPackage, vorgewaehltesPaket, unterrichtsformen, selectedFormId, signupFeeAmountCents, tarifAufnahmegebuehr, includeSignupFee]);

  useEffect(() => {
    if (!open || step !== 1) return;
    if (selectedFormId && teacherId) setStep1ActiveSection('terminplan');
    else setStep1ActiveSection('unterricht');
  }, [open, step, selectedFormId, teacherId]);

  const selectedSlotForDateStep: SelectedSlot = useMemo(() => {
    if (!weekday || !startTime) return null;
    const end = addMinutes(startTime, durationMin || 30);
    return {
      dayOfWeek: weekday,
      startTime: `${startTime}:00`,
      endTime: `${end}:00`,
    };
  }, [weekday, startTime, durationMin]);

  const suggestedStartDate = useMemo(() => {
    if (!selectedSlotForDateStep) return '';
    const today = new Date();
    const currentDow = ((today.getDay() + 6) % 7) + 1;
    let daysToAdd = selectedSlotForDateStep.dayOfWeek - currentDow;
    if (daysToAdd < 0) daysToAdd += 7;
    const target = new Date(today);
    target.setDate(today.getDate() + daysToAdd);
    return toApiDate(target);
  }, [selectedSlotForDateStep]);

  // Unterrichtsbeginn frühestens heute und nicht vor dem Start des übernommenen Kurses.
  const minStartDate = useMemo(() => {
    const today = toApiDate(new Date());
    return courseStartDate && courseStartDate > today ? courseStartDate : today;
  }, [courseStartDate]);

  const datePageModel = useMemo(
    () => computeDatePageModel(selectedSlotForDateStep, nextAvailableDate, datePage, datesPerPage),
    [selectedSlotForDateStep, nextAvailableDate, datePage]
  );

  // Erster zulässiger Termin: Vorschlag bzw. Kurstermin, aber nicht vor minStartDate.
  const proposedStartDate = useMemo(() => {
    const proposed = nextAvailableDate || suggestedStartDate;
    if (!proposed || proposed >= minStartDate) return proposed;
    return datePageModel?.pageDates.find((d) => d.d >= minStartDate)?.d ?? proposed;
  }, [nextAvailableDate, suggestedStartDate, minStartDate, datePageModel]);

  const step2HintText = useMemo(() => {
    if (!proposedStartDate) return '';
    return `Vorschlag: ${formatDate(proposedStartDate)}`;
  }, [proposedStartDate]);

  /**
   * Liegt der neue Kurs noch auf dem Termin der Probestunde? Dann sagt die
   * Kachel das, statt Uhrzeit und Wochentag ein zweites Mal zu wiederholen
   * (T-0011).
   */
  const gleicherTerminWieProbestunde = useMemo(() => {
    const vorgabeTag = defaults?.weekday;
    const vorgabeZeit = defaults?.startTime?.slice(0, 5);
    if (!vorgabeTag || !vorgabeZeit) return false;
    return weekday === vorgabeTag && startTime.slice(0, 5) === vorgabeZeit;
  }, [defaults?.weekday, defaults?.startTime, weekday, startTime]);

  // PreviewEvent für Terminplan berechnen
  const previewEvent: PreviewEvent | null = useMemo(() => {
    if (!weekday || !startTime || !durationMin) return null;
    const formName = selectedForm?.name || 'Unterricht';
    // „Einzelunterricht 30 Minuten" — die Dauer steht meist schon im Namen.
    const formLabel = /\d/.test(formName) ? formName : `${formName} · ${durationMin} Minuten`;
    const endTime = (() => {
      const [hh, mm] = startTime.split(':').map(Number);
      const totalMinutes = hh * 60 + mm + durationMin;
      const endH = Math.floor(totalMinutes / 60) % 24;
      const endM = totalMinutes % 60;
      return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
    })();

    return {
      dayOfWeek: weekday,
      startTime: startTime.length >= 5 ? startTime.slice(0, 5) : startTime,
      endTime,
      title: formLabel,
      subtitle: trialData?.studentName || 'Schüler',
      fachName: trialData?.fachName || formName,
      // Grün wie der Termin, den er ablöst — die Probestunde selbst wird
      // ausgeblendet, sonst stünden zwei Termine nebeneinander.
      color: 'bg-emerald-200 border-emerald-600 text-emerald-950',
      headline: gleicherTerminWieProbestunde ? 'Gleicher Termin wie Probestunde' : undefined,
    };
  }, [weekday, startTime, durationMin, selectedForm, trialData, gleicherTerminWieProbestunde]);

  const historyTemplateVars = useMemo(() => {
    const studentName = (trialData?.studentName || 'Teilnehmer').toString();
    const dateLabel = startDate ? formatDate(startDate) : '';
    const endTime = (previewEvent?.endTime || (startTime ? addMinutes(startTime, durationMin || 30) : '')).slice(0, 5);
    return {
      studentName,
      date: dateLabel,
      startTime: startTime.slice(0, 5),
      endTime,
      roomName: '',
      roomClause: '',
    };
  }, [durationMin, previewEvent?.endTime, startDate, startTime, trialData?.studentName]);

  const computedCreateHistoryText = useMemo(() => {
    const studentName = historyTemplateVars.studentName || 'Teilnehmer';
    const dateText = startDate ? formatDate(startDate) : 'offenes Datum';
    const timeText = startTime ? `${startTime.slice(0, 5)} Uhr` : 'offene Uhrzeit';
    return `Anmeldung für ${studentName} erstellt (${dateText}, ${timeText}).`;
  }, [historyTemplateVars.studentName, startDate, startTime]);

  useEffect(() => {
    if (!open) return;
    if (createHistoryTouched) return;
    setCreateHistoryText(computedCreateHistoryText);
  }, [open, createHistoryTouched, computedCreateHistoryText]);

  useEffect(() => {
    if (!open || createHistoryTouched) return;
    if (!startDate || !startTime) return;
    let cancelled = false;
    const renderTemplate = async () => {
      try {
        const rendered = await historyTemplatesApi.render({
          template_key: 'enrollment.created.history',
          standort_id: standortId || undefined,
          vars: historyTemplateVars,
        });
        if (cancelled) return;
        if (rendered.history) setCreateHistoryText(rendered.history);
        if (rendered.task) {
          setHistoryTaskPreview({
            enabled: true,
            description: rendered.task.description,
            dueAt: rendered.task.dueAt,
          });
        } else {
          setHistoryTaskPreview({ enabled: false });
        }
      } catch {
        if (!cancelled) setHistoryTaskPreview({ enabled: false });
      }
    };
    void renderTemplate();
    return () => {
      cancelled = true;
    };
  }, [open, createHistoryTouched, historyTemplateVars, startDate, startTime, standortId]);

  useEffect(() => {
    if (!open || step !== 2) return;
    if (!selectedSlotForDateStep) return;
    if (dateManuallySelected) return;
    const next = proposedStartDate;
    if (!next) return;
    if (!startDate || startDate < next) {
      setStartDate(next);
    }
  }, [open, step, selectedSlotForDateStep, startDate, proposedStartDate, dateManuallySelected]);

  // Slot im Terminplan auswählen
  const handleSelectSlot = (slot: { date: string; dayOfWeek: number; startTime: string; endTime: string }) => {
    if (!selectedFormId || !durationMin) {
      toast.error('Bitte wählen Sie zuerst eine Unterrichtsform aus');
      return;
    }
    setStartDate('');
    setWeekday(slot.dayOfWeek);
    setStartTime(slot.startTime.slice(0, 5));
    setDatePage(0);
    setNextAvailableDate(slot.date || '');
    setDateManuallySelected(false);
    setSelectedCourseId(undefined);
    setCourseStartDate('');
    setSelectedEventId(null);
  };

  // Event (Kurs) auswählen
  const handleSelectEvent = async (event: TeacherTimetableSelectEvent) => {
    if (event.type !== 'pattern' || !event.courseId) return;
    try {
      const course = courseOptions.find((c) => c.id === event.courseId) || await kurseApi.get(event.courseId);
      setSelectedCourseId(course.id);
      setSelectedEventId(event.id);
      setCourseStartDate(event.startsAt || '');
      if (event.dayOfWeek) setWeekday(event.dayOfWeek);
      if (event.startTime) setStartTime(event.startTime.slice(0, 5));
      const firstDate = event.startsAt && event.date && event.startsAt > event.date ? event.startsAt : event.date;
      setStartDate(firstDate || '');
      setNextAvailableDate(firstDate || suggestedStartDate);
      setDatePage(0);
      setDateManuallySelected(false);
      setDurationMin(course.dauer_min);
      // Die Unterrichtsform des Kurses zählt, nicht seine Dauer: Einzelunterricht und
      // Zweiergruppe dauern beide 30 Minuten, kosten aber Verschiedenes — über die Dauer
      // gesucht wurde aus einer Gruppe stillschweigend Einzelunterricht.
      const matchingForm =
        (course.unterrichtsform_id
          ? unterrichtsformen.find((f) => f.id === course.unterrichtsform_id)
          : undefined) ?? unterrichtsformen.find((f) => f.dauer === course.dauer_min);
      if (matchingForm) {
        setSelectedFormId(matchingForm.id);
        setSelectedForm(matchingForm);
      }
      toast.success('Bestehender Kurs wurde übernommen');
    } catch {
      toast.error('Kurs konnte nicht geladen werden');
    }
  };

  // Validierung pro Schritt
  const canProceedStep1 = useMemo(() => {
    return !!selectedFormId && !!weekday && !!startTime && !!durationMin;
  }, [selectedFormId, weekday, startTime, durationMin]);
  const canProceedStep2 = useMemo(() => !!startDate && startDate >= minStartDate, [startDate, minStartDate]);

  // Navigation
  const handleNext = () => {
    if (step === 1 && !canProceedStep1) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }
    if (step < 5) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Absenden
  useEffect(() => {
    if (step !== 3 || !selectedFormId) return;
    let cancelled = false;
    billingApi
      .enrollmentOptions({
        unterrichtsformId: selectedFormId,
        standortId: standortId > 0 ? standortId : undefined,
        startDate: startDate || undefined,
      })
      .then((options) => {
        if (cancelled) return;
        setBillingOptions(options);
        setBillingInterval((current) =>
          current && options.allowedIntervals?.includes(current) ? current : options.defaultInterval,
        );
        const packages = options.contingentTariffs ?? [];
        setContingentTariffId((current) =>
          current && packages.some((tariff) => tariff.id === current)
            ? current
            : (packages.find((tariff) => tariff.id === options.tariff?.id)?.id ?? packages[0]?.id),
        );
      })
      .catch(() => {
        // Ohne Optionen (z. B. fehlende Berechtigung) bleibt die Paket-Ansicht.
        if (!cancelled) setBillingOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [step, selectedFormId, standortId, startDate]);

  const handleSubmit = async () => {
    if (!canProceedStep1) {
      toast.error('Bitte füllen Sie alle Felder im ersten Schritt aus');
      return;
    }

    setIsLoading(true);
    try {
      const debugFees = new URLSearchParams(window.location.search).has('debugFees');
      const selectedStartPackageId = includeStartPackage
        ? (specialPackagePrices[0]?.package?.id || undefined)
        : undefined;
      const isPeriodic = billingOptions?.model === 'periodic';
      // Kontingent mit Tarif: Paket aus dem Wizard statt Altpaket (ADR-0003, Phase 5).
      const usesContingentTariff =
        billingOptions?.model === 'contingent' && (billingOptions.contingentTariffs ?? []).length > 0;
      const skipsLegacyPackage = isPeriodic || usesContingentTariff;
      const payload: CreateEnrollmentPayload = {
        // Auch bei einem übernommenen Kurs festhalten: Das Lehrerhonorar hängt an der Art
        // der Unterrichtsform.
        unterrichtsformId: selectedFormId,
        courseId: selectedCourseId || undefined,
        weekday,
        startTime: startTime.length === 5 ? `${startTime}:00` : startTime,
        intervalWeeks: interval,
        startDate,
        durationMin,
        sendToStudent,
        subprofileId,
        teacherId,
        debugFees,
        studentSelectsPackage: true,
        startpaketSelected: skipsLegacyPackage ? false : includeStartPackage,
        contingentPackageId: skipsLegacyPackage ? undefined : selectedStartPackageId,
        billingIntervalMonths: isPeriodic ? billingInterval : undefined,
        billingTariffId: isPeriodic
          ? (billingOptions?.tariff?.id ?? undefined)
          : usesContingentTariff
            ? contingentTariffId
            : undefined,
        couponCode: einmalcode || undefined,
        // Nur ein Folgepaket: Ein früher als „gewählt“ markiertes Startpaket verdrängte
        // sonst weiter das Folgepaket (T-0247); das Startpaket regelt startpaketSelected.
        vorgewaehltesPaket:
          skipsLegacyPackage || !followupPackagePrices.some((pp) => pp.package.id === vorgewaehltesPaket)
            ? undefined
            : vorgewaehltesPaket,
        // Die Zahlweise je Paket war bisher reine Anzeige; jetzt belegt sie die
        // Auswahl des Kunden vor (T-0011).
        zahlweiseJePaket: skipsLegacyPackage
          ? undefined
          : Object.fromEntries(
              Object.entries(paymentTypeByPackage).map(([id, art]) => [id, art === 'raten' ? 'installment' : 'immediate']),
            ) as Record<string, 'immediate' | 'installment'>,
        // Abgewählt = keine Aufnahmegebühr; angehakt = nach dem Modus des Pakets.
        // Beitrag/Kontingent-Tarif: eigener Schalter, standardmäßig aus (T-0171).
        signupFeeSelected: skipsLegacyPackage ? tarifAufnahmegebuehr : includeSignupFee,
        emailContent: sendToStudent ? (emailContent || undefined) : undefined,
        emailSubject: sendToStudent ? (emailSubject || undefined) : undefined,
      };

      const created = await createEnrollment(trialId, payload);

      // Historie analog Trial: editierbaren Eintrag beim Teilnehmer speichern.
      const historyDescription = createHistoryText.trim();
      if (historyDescription !== '') {
        const subIdRaw = String(payload.subprofileId || '').trim();
        const [wpPart, subPart] = subIdRaw.includes('-') ? subIdRaw.split('-', 2) : [subIdRaw, ''];
        const wpUserId = Number.parseInt(wpPart || '', 10) || 0;
        const subIndex = subPart ? (Number.parseInt(subPart, 10) || null) : null;
        if (wpUserId > 0) {
          try {
            await api.post(`/api/personen/${wpUserId}/history`, {
              type: 'enrollment',
              description: historyDescription,
              meta: {
                enrollmentId: created.id,
                trialId: Number(trialId) || null,
              },
              subIndex,
            });
          } catch {
            toast.error('Historieneintrag konnte nicht gespeichert werden');
          }
        }
      }

      if (debugFees) {
        // eslint-disable-next-line no-console
        console.log('[sidigo] feeDebug:', (created as any)?.feeDebug);
      }

      // Backend versucht bereits automatisch zu senden, wenn sendToStudent=true.
      // Wenn das nicht geklappt hat (Status bleibt 'draft'), versuchen wir es explizit,
      // damit Fehler (z.B. fehlendes ENROLLMENTS_TOKEN_SECRET / Mailer-Config) im UI sichtbar werden.
      let sentOk = true;
      if (payload.sendToStudent) {
        if (created.status !== 'sent') {
          try {
            await sendEnrollmentOffer(created.id);
          } catch (e: any) {
            sentOk = false;
            toast.error(e?.message || 'E-Mail konnte nicht gesendet werden');
          }
        }
      }

      const tat = bestehendeAnmeldung ? 'gespeichert' : 'erstellt';
      if (payload.sendToStudent && !sentOk) {
        toast.success(`Anmeldung ${tat} (Entwurf).`);
      } else {
        toast.success(
          payload.sendToStudent
            ? `Anmeldung ${tat} und E-Mail versendet`
            : `Anmeldung erfolgreich ${tat}`,
        );
      }

      onOpenChange(false);
      if (onCreated) onCreated(created.id);
    } catch (error: any) {
      toast.error(
        error?.message
          || (bestehendeAnmeldung
            ? 'Fehler beim Speichern der Anmeldung'
            : 'Fehler beim Erstellen der Anmeldung'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Reset beim Schließen
  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelectedFormId(defaults?.unterrichtsformId);
      setWeekday(defaults?.weekday);
      setStartTime(defaults?.startTime?.slice(0, 5) || '');
      setStartDate(defaults?.startDate || '');
      setInterval(defaults?.intervalWeeks || 1);
      setIncludeStartPackage(true);
      setIncludeSignupFee(true);
      setTarifAufnahmegebuehr(false);
      setTarifAufnahmegebuehr(false);
      setSendToStudent(true);
      setSkipStudentEmail(false);
      setSkipStudentSms(true);
      setCreateHistoryText('');
      setCreateHistoryTouched(false);
      setHistoryTaskPreview(null);
      setSelectedEventId(null);
      setStep1ActiveSection('unterricht');
      setSelectedCourseId(undefined);
      setCourseStartDate('');
      setCourseOptions([]);
      setDatePage(0);
      setNextAvailableDate('');
      setDateManuallySelected(false);
      geladenePaketeRef.current = null;
      setPaymentTypeByPackage({});
      setEinmalcode('');
      setVorgewaehltesPaket('');
      setTeacherAvailability([]);
      setBillingOptions(null);
      setBillingInterval(undefined);
    }
  }, [open, defaults]);

  const weekdayLabel = weekday ? ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'][weekday - 1] : '—';
  const timeRangeLabel = useMemo(() => {
    if (!startTime || !durationMin) return '—';
    const [hh, mm] = startTime.split(':').map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return `${startTime} Uhr`;
    const total = (hh * 60) + mm + durationMin;
    const endH = Math.floor(total / 60) % 24;
    const endM = total % 60;
    return `${startTime.slice(0, 5)} bis ${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')} Uhr`;
  }, [durationMin, startTime]);
  const specialPackagePrices = useMemo(() => packagePrices.filter((pp) => pp.package.special), [packagePrices]);
  const followupPackagePrices = useMemo(() => packagePrices.filter((pp) => !pp.package.special), [packagePrices]);
  const suppressFollowupSignupFee = specialPackagePrices.length > 0 && includeStartPackage;
  const selectedCourseName = selectedCourseId ? (courseOptions.find((c) => c.id === selectedCourseId)?.name || `Kurs #${selectedCourseId}`) : null;
  const selectedFormName = selectedForm?.name || '—';
  const intervalLabel = interval === 1 ? 'wöchentlich' : `alle ${interval} Wochen`;
  const hasDurationInName = useMemo(() => {
    if (!selectedFormName || !durationMin) return false;
    const nameLower = selectedFormName.toLowerCase();
    return nameLower.includes(`${durationMin}`) || nameLower.includes('min');
  }, [durationMin, selectedFormName]);

  return open ? (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title={bestehendeAnmeldung ? 'Anmeldung anpassen' : 'Anmeldung erstellen'}
      description={`Schritt ${step} von 5`}
      currentStep={step}
      steps={[
        { label: 'Terminplan' },
        { label: 'Datum' },
        { label: 'Pakete' },
        { label: 'E-Mail' },
        { label: 'Übersicht' },
      ]}
      onBack={step > 1 ? handleBack : undefined}
      canBack={step > 1 && !isLoading}
      backLabel="Zurück"
      onNext={step < 5 ? handleNext : undefined}
      canNext={step === 1 ? canProceedStep1 : step === 2 ? canProceedStep2 : true}
      nextLabel="Weiter"
      onPrimary={step === 5 ? handleSubmit : undefined}
      primaryLabel={
        isLoading
          ? (bestehendeAnmeldung ? 'Wird gespeichert …' : 'Wird erstellt …')
          : (bestehendeAnmeldung ? 'Speichern' : 'Anlegen')
      }
      canPrimary={step === 5 && !isLoading}
      isLoading={isLoading}
      contentClassName={step === 1 ? 'w-[98vw] h-[96vh] max-w-[98vw] max-h-[96vh]' : undefined}
      // Passt Schritt 1 nicht ins Fenster (kleinere Bildschirme), muss er scrollen —
      // mit overflow-hidden waren Terminplan und Turnus unten unerreichbar (T-0228).
      bodyClassName={step === 1 ? 'px-6 overflow-y-auto' : 'px-6'}
    >
      <div className="py-4">

        {/* Schritt 1: Terminplan-Auswahl */}
        {step === 1 && (
          <WizardCard className="my-2 flex h-full min-h-0 flex-col">
            <div className={cn('flex h-full min-h-0 flex-col', step1UnterrichtCollapsed ? 'gap-2' : 'gap-4')}>
              {step1UnterrichtCollapsed ? (
                <button
                  type="button"
                  className={cn(
                    'group flex w-full items-center justify-between rounded-xl border bg-muted/20 px-3 py-2 text-left transition-colors',
                    step1ActiveSection === 'unterricht' ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'opacity-90'
                  )}
                  onClick={() => setStep1ActiveSection('unterricht')}
                >
                  <span className="truncate text-xs leading-tight text-muted-foreground">
                    <span className="font-semibold uppercase tracking-wide">Unterricht</span>{' '}
                    <span>{selectedCourseName || selectedFormName}</span>
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-normal ease-standard motion-safe:group-hover:translate-y-0.5 motion-safe:group-hover:rotate-12 group-hover:text-foreground" />
                </button>
              ) : (
                <WizardSection
                  className={cn('transition-colors', step1ActiveSection === 'unterricht' ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'opacity-90')}
                  title={
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto w-full justify-start rounded-md px-0 py-0 text-left normal-case font-normal hover:bg-transparent"
                      onClick={() => setStep1ActiveSection(step1ActiveSection === 'unterricht' ? 'terminplan' : 'unterricht')}
                    >
                      <span className="uppercase tracking-wide text-muted-foreground">Unterricht</span>
                    </Button>
                  }
                >
                  <WizardKVGrid>
                    <WizardKVItem label="Unterrichtsform">
                      <Select
                        value={selectedFormId?.toString() || ''}
                        onValueChange={(v) => {
                          setSelectedFormId(v ? Number(v) : undefined);
                          setSelectedCourseId(undefined);
                          setCourseStartDate('');
                          setSelectedEventId(null);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Unterrichtsform auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {unterrichtsformen.map((form) => (
                            <SelectItem key={form.id} value={form.id.toString()}>
                              {form.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </WizardKVItem>
                  </WizardKVGrid>
                </WizardSection>
              )}

              <WizardSection
                className={cn(
                  'transition-colors',
                  step1ActiveSection === 'terminplan' ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'opacity-90',
                  !step1TerminplanCollapsed ? 'flex min-h-0 flex-1 flex-col' : ''
                )}
                title={
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto w-full justify-start rounded-md px-0 py-0 text-left normal-case font-normal hover:bg-transparent"
                    onClick={() => setStep1ActiveSection(step1ActiveSection === 'terminplan' ? 'unterricht' : 'terminplan')}
                  >
                    <span className="uppercase tracking-wide text-muted-foreground">Terminplan</span>
                  </Button>
                }
              >
                {step1TerminplanCollapsed ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="interactive-lift h-auto w-full justify-start rounded-lg border bg-background/60 p-3 text-left transition-colors hover:bg-muted/30"
                    onClick={() => setStep1ActiveSection('terminplan')}
                  >
                    <div className="text-sm text-muted-foreground">Klicken, um den Terminplan zu öffnen.</div>
                  </Button>
                ) : null}
                {!step1TerminplanCollapsed && teacherId ? (
                  <div className="flex min-h-[340px] flex-1 flex-col">
                    <div className="relative min-h-0 flex-1 rounded-lg border">
                      <WizardScheduleGrid
                        teacherId={teacherId}
                        initialViewMode="week"
                        allowOccupiedSelection={true}
                        height={1200}
                        availabilitySlots={teacherAvailability}
                        selectedSlot={weekday && startTime ? {
                          dayOfWeek: weekday,
                          startTime: startTime.length >= 5 ? startTime.slice(0, 5) : startTime,
                          endTime: previewEvent?.endTime || '',
                        } : null}
                        previewEvent={previewEvent}
                        hideEventIds={versteckteTermine}
                        onSelectSlot={(slot) => {
                          if (!slot.date) return;
                          handleSelectSlot({
                            date: slot.date,
                            dayOfWeek: slot.dayOfWeek,
                            startTime: slot.startTime,
                            endTime: slot.endTime,
                          });
                        }}
                        onSelectEvent={handleSelectEvent}
                      />
                    </div>
                    {teacherAvailability.length > 0 ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Freigabezeiten des Lehrers sind eingeblendet (außerhalb markiert).
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {!step1TerminplanCollapsed && !teacherId ? (
                  <div className="text-sm text-muted-foreground">Kein Lehrer zugewiesen. Bitte in der Probestunde einen Lehrer setzen.</div>
                ) : null}
                {selectedEventId ? (
                  <div className="mt-3 rounded-md border bg-blue-50 p-3 text-sm text-muted-foreground">
                    Bestehender Kurs wird für diese Anmeldung verwendet.
                  </div>
                ) : null}
              </WizardSection>
            </div>
            <div className="mt-4 max-w-xs">
              <Label>Turnus</Label>
              <Select value={String(interval)} onValueChange={(v) => setInterval(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Wöchentlich</SelectItem>
                  <SelectItem value="2">14-tägig</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </WizardCard>
        )}

        {/* Schritt 2: Datum */}
        {step === 2 && (
          <StepDateSelection
            selectedSlot={selectedSlotForDateStep}
            step2HintText={step2HintText}
            datePageModel={datePageModel}
            date={startDate}
            startTime={startTime}
            teacherDayLoading={false}
            teacherDayError=""
            hasTeacherCollision={false}
            onPrevPage={() => setDatePage((prev) => prev - 1)}
            onNextPage={() => setDatePage((prev) => prev + 1)}
            onSelectDate={(nextDate, nextStartTime) => {
              setStartDate(nextDate);
              setStartTime(nextStartTime);
              setDateManuallySelected(true);
            }}
            addMinutes={addMinutes}
            intro="Wählen Sie den ersten Unterrichtstermin aus."
            minDate={minStartDate}
            durationMin={durationMin || 30}
          />
        )}

        {/* Schritt 3: Paketauswahl */}
        {step === 3 && (() => {
          const renderPackageCard = (pp: PackagePrice, isFollowup: boolean) => {
            const pkg = pp.package;
            const paymentType = paymentTypeByPackage[pkg.id] ?? 'sofort';
            const totalImmediateCents = pkg.totalCentsImmediate ?? pkg.immediatePriceCents ?? pp.totalCents;
            const totalInstallmentCents = pkg.totalCentsInstallment ?? pkg.installmentPriceCents ?? totalImmediateCents;
            const hasInstallments = Boolean(
              (pkg.installmentEnabled ?? true) &&
              (pkg.installmentMonths ?? 0) > 0 &&
              totalInstallmentCents > 0
            );
            const configuredDiscountPercent = Math.max(
              0,
              Math.round(Number(pkg.immediateDiscountPercent ?? pkg.discountPercent ?? 0))
            );
            const derivedDiscountPercent = totalInstallmentCents > totalImmediateCents
              ? Math.round((1 - (totalImmediateCents / totalInstallmentCents)) * 100)
              : 0;
            const immediateDiscountPercent = configuredDiscountPercent > 0
              ? configuredDiscountPercent
              : derivedDiscountPercent;

            const originalImmediateCents = totalInstallmentCents > totalImmediateCents
              ? totalInstallmentCents
              : immediateDiscountPercent > 0
                ? Math.round(totalImmediateCents / (1 - (immediateDiscountPercent / 100)))
                : totalImmediateCents;

            const includeSignupFeeForPackage = includeSignupFee && !(isFollowup && suppressFollowupSignupFee);
            const signupFeeCents = includeSignupFeeForPackage && signupFeeAmountCents > 0 ? signupFeeAmountCents : 0;

            const baseTotalCents = paymentType === 'raten' && hasInstallments
              ? totalInstallmentCents
              : totalImmediateCents;
            const baseOriginalCents = paymentType === 'sofort' ? originalImmediateCents : totalInstallmentCents;
            const displayTotalCents = baseTotalCents + signupFeeCents;
            const displayOriginalTotalCents = baseOriginalCents + signupFeeCents;
            const displayUnitCents = pkg.units > 0 ? Math.round(baseTotalCents / pkg.units) : 0;
            const displayOriginalUnitCents = pkg.units > 0 ? Math.round(baseOriginalCents / pkg.units) : 0;

            const price = (displayTotalCents / 100).toFixed(2);
            const originalPrice = (displayOriginalTotalCents / 100).toFixed(2);
            const unitPrice = (displayUnitCents / 100).toFixed(2);
            const originalUnitPrice = (displayOriginalUnitCents / 100).toFixed(2);
            const hasDiscount = paymentType === 'sofort' && baseOriginalCents > baseTotalCents;
            const isHighlighted = pkg.highlighted;
            const highlightLabel = isFollowup ? (pkg.highlightLabel || 'Hero') : (pkg.highlightLabel || 'meistgebucht');
            const isSpecial = pkg.special === true;

            return (
              <Card
                key={pkg.id}
                className={cn(
                  'h-full border-border',
                  isHighlighted ? 'ring-1 ring-red-500/60' : '',
                  isSpecial ? 'bg-primary/5' : '',
                  (isFollowup ? vorgewaehltesPaket === pkg.id : includeStartPackage) ? 'ring-2 ring-primary' : ''
                )}
              >
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2 leading-tight">
                    {isSpecial && <span className="text-xs px-2 py-0.5 rounded bg-primary text-primary-foreground mr-1">Special</span>}
                    {isSpecial ? 'Special: ' : ''}{pkg.name}
                    {isHighlighted && (
                      <span className="text-xs px-2 py-0.5 rounded bg-red-700 text-white">
                        {highlightLabel}
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs leading-tight">
                    {pkg.units} Einheiten{hasDiscount ? ` · ${immediateDiscountPercent}% Rabatt` : ''}
                  </CardDescription>
                  {/* Im Gespräch gewählt (T-0230): die Website wählt es vor. Nur unter den
                      Folgepaketen — das Startpaket kommt davor, über „Startpaket hinzufügen“;
                      als vierte Wahl daneben verdrängte es das Folgepaket (T-0247). */}
                  {isFollowup ? (
                    <Button
                      type="button"
                      size="sm"
                      variant={vorgewaehltesPaket === pkg.id ? 'default' : 'outline'}
                      className="mt-2 h-7 text-[11px]"
                      aria-pressed={vorgewaehltesPaket === pkg.id}
                      onClick={() => setVorgewaehltesPaket((jetzt) => (jetzt === pkg.id ? '' : pkg.id))}
                    >
                      {vorgewaehltesPaket === pkg.id ? 'Vom Kunden gewählt' : 'Kunde nimmt dieses Paket'}
                    </Button>
                  ) : null}
                </CardHeader>
                <CardContent className="py-3">
                  <div className="space-y-2 text-xs">
                    {hasInstallments && (
                      <div className="inline-flex rounded-md border bg-muted/30 p-0.5">
                        <button
                          type="button"
                          onClick={() => setPaymentTypeByPackage((prev) => ({ ...prev, [pkg.id]: 'sofort' }))}
                          className={cn(
                            'px-2.5 py-1 rounded text-[11px]',
                            paymentType === 'sofort' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                          )}
                        >
                          Sofort
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentTypeByPackage((prev) => ({ ...prev, [pkg.id]: 'raten' }))}
                          className={cn(
                            'px-2.5 py-1 rounded text-[11px]',
                            paymentType === 'raten' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                          )}
                        >
                          Raten
                        </button>
                      </div>
                    )}
                    {hasInstallments ? (
                      <div className="text-muted-foreground">
                        {paymentType === 'raten' ? (
                          <>
                            {installmentLabel(totalInstallmentCents, pkg.installmentMonths || 1)} monatlich
                            <span className="block">Verbrauchsabhängige Ratenzahlung. Je Rate 3 Termine.</span>
                          </>
                        ) : (
                          // „beide Möglichkeiten … anzeigen" (T-0011): Wer sofort zahlt,
                          // soll trotzdem sehen, was die Rate kostet.
                          <>Alternativ in Raten: {installmentLabel(totalInstallmentCents, pkg.installmentMonths || 1)} monatlich</>
                        )}
                      </div>
                    ) : null}
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Preis/Einheit</span>
                      <div className="flex items-center gap-2">
                        {hasDiscount && (
                          <span className="text-muted-foreground line-through text-xs">
                            {originalUnitPrice} {pp.currency}
                          </span>
                        )}
                        <span className={hasDiscount ? 'text-primary font-semibold' : ''}>
                          {unitPrice} {pp.currency}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center font-semibold">
                      <span className="text-muted-foreground font-normal">Gesamt</span>
                      <div className="flex items-center gap-2">
                        {hasDiscount && (
                          <span className="text-muted-foreground line-through text-xs font-normal">
                            {originalPrice} {pp.currency}
                          </span>
                        )}
                        <span className={hasDiscount ? 'text-primary' : ''}>
                          {price} {pp.currency}
                        </span>
                      </div>
                    </div>
                    {signupFeeCents > 0 ? (
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Aufnahmegebühr</span>
                        <span>{(signupFeeCents / 100).toFixed(2).replace('.', ',')} {pp.currency}</span>
                      </div>
                    ) : null}
                    {hasDiscount ? (
                      <div className="text-muted-foreground">
                        Ersparnis: {((baseOriginalCents - baseTotalCents) / 100).toFixed(2).replace('.', ',')} {pp.currency}
                      </div>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          };

          // Den Einmalcode gab es nur beim alten Paketaufbau — bei Beitrag und
          // Kontingent-Tarif fehlte er (T-0011).
          const einmalcodeFeld = (
            <EinmalcodeFeld wpUserId={kundenWpUserId} code={einmalcode} onCode={setEinmalcode} />
          );

          // Bei Tarifen wurde die Gebühr bisher stillschweigend gebucht (T-0171):
          // jetzt nur, wenn hier angeschaltet — dann steht sie auch im Angebot.
          const aufnahmegebuehrSchalter = signupFeeAmountCents > 0 ? (
            <div className="flex items-center gap-3 rounded-lg border px-4 py-3">
              <Switch
                id="enrollment-tariff-signup-fee-toggle"
                checked={tarifAufnahmegebuehr}
                onCheckedChange={(checked) => setTarifAufnahmegebuehr(checked === true)}
              />
              <Label htmlFor="enrollment-tariff-signup-fee-toggle">
                Aufnahmegebühr berechnen ({formatCents(signupFeeAmountCents)}, einmalig zum Vertragsstart)
              </Label>
            </div>
          ) : null;

          if (billingOptions?.model === 'periodic') {
            return (
              <div className="space-y-4">
                <PeriodicTariffStep
                  options={billingOptions}
                  interval={billingInterval}
                  onIntervalChange={setBillingInterval}
                  selectionLabel={`${trialData?.fachName || '—'} | ${selectedFormName || '—'} / ${intervalLabel}`}
                />
                {aufnahmegebuehrSchalter}
                {einmalcodeFeld}
              </div>
            );
          }

          if (billingOptions?.model === 'contingent' && (billingOptions.contingentTariffs ?? []).length > 0) {
            return (
              <div className="space-y-4">
                <ContingentTariffStep
                  tariffs={billingOptions.contingentTariffs ?? []}
                  selectedId={contingentTariffId}
                  onSelect={setContingentTariffId}
                  selectionLabel={`${trialData?.fachName || '—'} | ${selectedFormName || '—'} / ${intervalLabel}`}
                />
                {aufnahmegebuehrSchalter}
                {einmalcodeFeld}
              </div>
            );
          }

          return (
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm">
                Gewählt: <span className="text-foreground">{trialData?.fachName || '—'} | {selectedFormName || '—'} / {intervalLabel}</span>
              </div>

              <div className="rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground">
                Paket-Informationen
              </div>
              <p className="text-sm text-muted-foreground">
                Der Kunde wählt sein Paket in der Anmeldung selbst. Sofort- oder Ratenzahlung kannst Du hier je Paket vorgeben — sie ist beim Kunden vorausgewählt, er kann sie noch ändern.
              </p>
              {specialPackagePrices.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="enrollment-start-package-toggle"
                      checked={includeStartPackage}
                      onCheckedChange={(checked) => setIncludeStartPackage(checked === true)}
                    />
                    <Label htmlFor="enrollment-start-package-toggle">Startpaket hinzufügen</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      id="enrollment-signup-fee-toggle"
                      checked={includeSignupFee}
                      onCheckedChange={(checked) => setIncludeSignupFee(checked === true)}
                    />
                    <Label htmlFor="enrollment-signup-fee-toggle">Aufnahmegebühr hinzufügen</Label>
                  </div>
                  {includeStartPackage ? (
                    <div className="grid grid-cols-1 gap-3">
                      {specialPackagePrices.map((pp) => renderPackageCard(pp, false))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {einmalcodeFeld}

              <div className="rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground">Folgepakete</div>
              <p className="text-sm text-muted-foreground">
                Die Auswahl kann jederzeit im Kundenkonto angepasst werden und gilt für Folgepakete.
              </p>
              {followupPackagePrices.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {followupPackagePrices.map((pp) => renderPackageCard(pp, true))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground p-4 text-center">
                  Keine weiterführenden Pakete verfügbar.
                </div>
              )}
            </div>
          );
        })()}

        {/* Schritt 4: Kommunikation */}
        {step === 4 && composerGroups.length > 0 && standortId > 0 && (
          <CommunicationComposer
            active={open && step === 4}
            standortId={standortId}
            groups={composerGroups}
            onDraftsChange={({ groups: g }) => {
              const s = g['student'];
              if (s) {
                setSendToStudent(!!s.selectedChannels.email);
                setSkipStudentEmail(!s.selectedChannels.email);
                setSkipStudentSms(!s.selectedChannels.sms);
                setEmailSubject(s.email.subjectTpl || '');
                setEmailContent(s.email.htmlTpl || '');
              }
            }}
          />
        )}
        {step === 4 && composerGroups.length > 0 && standortId <= 0 ? (
          <CommunicationStandortHint />
        ) : null}

        {/* Schritt 5: Übersicht */}
        {step === 5 && (
          <WizardCard>
            <WizardSection title="Termin">
              <WizardKVGrid>
                {selectedCourseName ? <WizardKVItem label="Kurs">{selectedCourseName}</WizardKVItem> : null}
                <WizardKVItem label={getSubjectLabels().singular}>{trialData?.fachName || '—'}</WizardKVItem>
                <WizardKVItem label="Unterrichtsform">{selectedFormName}</WizardKVItem>
                <WizardKVItem label="Lehrer">{trialData?.teacherName || '—'}</WizardKVItem>
                <WizardKVItem label="Wochentag">{weekdayLabel}</WizardKVItem>
                <WizardKVItem label="Zeit">{timeRangeLabel}</WizardKVItem>
                <WizardKVItem label="Startdatum">{startDate ? formatDate(startDate) : '—'}</WizardKVItem>
                {!hasDurationInName ? <WizardKVItem label="Dauer">{durationMin ? `${durationMin} Minuten` : '—'}</WizardKVItem> : null}
                <WizardKVItem label="Turnus">{interval === 1 ? 'Wöchentlich' : `Alle ${interval} Wochen`}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                <WizardKVItem label="Teilnehmer">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-foreground">{trialData?.studentName || 'Teilnehmer'}</span>
                    <span
                      className={`inline-flex h-6 items-center rounded-full border px-2.5 text-xs font-medium ${
                        !skipStudentEmail
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted bg-muted/70 text-muted-foreground'
                      }`}
                    >
                      E-Mail
                    </span>
                    <span
                      className={`inline-flex h-6 items-center rounded-full border px-2.5 text-xs font-medium ${
                        !skipStudentSms
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted bg-muted/70 text-muted-foreground'
                      }`}
                    >
                      SMS
                    </span>
                  </div>
                </WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
            <WizardSection title="Event-Historie">
              <div className="grid gap-4">
                <div>
                  <div className="mb-2 text-sm font-medium">Historieneintrag</div>
                  <Textarea
                    rows={5}
                    value={createHistoryText}
                    onChange={(e) => {
                      setCreateHistoryTouched(true);
                      setCreateHistoryText(e.target.value);
                    }}
                    placeholder="Historieneintrag…"
                  />
                  <div className="mt-2 text-xs text-muted-foreground">
                    Dieser Text wird beim Anlegen als Historieneintrag gespeichert und kann hier angepasst werden.
                  </div>
                </div>
                {historyTaskPreview?.enabled ? (
                  <div className="rounded-md border p-3 text-sm">
                    <div className="mb-1 text-xs text-muted-foreground">Aufgaben (Preview)</div>
                    <div className="space-y-1">
                      <div className="text-sm">{historyTaskPreview.description || 'Automatische Aufgabe aus Vorlage'}</div>
                      <div className="text-xs text-muted-foreground">
                        Fällig: {historyTaskPreview.dueAt || 'wird beim Speichern berechnet'}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </WizardSection>
          </WizardCard>
        )}

      </div>
    </WizardShell>
  ) : null;
};

export default EnrollmentWizardModal;
